/**
 * 💜 HEARTBEATS.FR - Landing Page
 * Design épuré, copywriting premium
 * ZÉRO photo de personnes - Icônes et gradients uniquement
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, MapPin, Shield, Sparkles, Users, Mic, MessageCircle, 
  ArrowRight, Menu, X, Check,
  Moon, Gamepad2, BadgeCheck, Crown, Volume2
} from 'lucide-react';

const HeartbeatsLanding = ({ onGetStarted, onLogin }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 12847,
    onlineNow: 202,
    conversations: 3420,
    matchesToday: 92
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/heartbeats/public-stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.log('Stats par défaut');
      }
    };
    loadStats();

    // Animation des stats
    const interval = setInterval(() => {
      setStats(prev => ({
        ...prev,
        onlineNow: Math.max(150, prev.onlineNow + Math.floor(Math.random() * 10) - 5),
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      window.location.href = '/auth?mode=signup';
    }
  };

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      window.location.href = '/auth?mode=login';
    }
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Les 4 piliers de Heartbeats
  const pillars = [
    {
      icon: Heart,
      gradient: 'from-[#dc2626] to-[#ec4899]',
      title: "Pas de swipe.",
      subtitle: "Des connexions.",
      desc: "On ne jette pas les gens comme des cartes. Ici, tu prends le temps de découvrir qui est vraiment en face de toi."
    },
    {
      icon: Mic,
      gradient: 'from-[#8b5cf6] to-[#6366f1]',
      title: "Parle avant",
      subtitle: "de matcher.",
      desc: "Salons vocaux en direct. Entends sa voix, son rire, son énergie. La vraie alchimie ne se lit pas, elle s'entend."
    },
    {
      icon: MapPin,
      gradient: 'from-[#22d3ee] to-[#0ea5e9]',
      title: "Près de toi.",
      subtitle: "Vraiment.",
      desc: "Pas à 500km. Les personnes que tu croises sont dans ton quartier, ta ville. Prêtes à prendre un café, pas un avion."
    },
    {
      icon: Shield,
      gradient: 'from-[#10b981] to-[#059669]',
      title: "100% vérifié.",
      subtitle: "0% fake.",
      desc: "Selfie + email obligatoires. Modération humaine. Ici, tu parles à de vraies personnes, pas à des bots."
    }
  ];

  // Fonctionnalités uniques
  const uniqueFeatures = [
    {
      icon: Moon,
      color: 'text-purple-400',
      bg: 'bg-purple-500/20',
      title: "Shadow Mode",
      desc: "Un quiz mystérieux pour révéler ta personnalité cachée. Découvre qui tu es vraiment en amour."
    },
    {
      icon: Volume2,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/20',
      title: "Salons Vocaux",
      desc: "Rejoins des discussions en live. Parle, écoute, connecte-toi avant même de voir un profil."
    },
    {
      icon: Gamepad2,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/20',
      title: "Mini-Jeux",
      desc: "Brise la glace avec des jeux fun. Gagne des récompenses. L'amour n'a pas besoin d'être ennuyeux."
    },
    {
      icon: BadgeCheck,
      color: 'text-green-400',
      bg: 'bg-green-500/20',
      title: "Badges Vérifiés",
      desc: "Prouve qui tu es. Les profils vérifiés sont mis en avant. La confiance se mérite."
    },
    {
      icon: Users,
      color: 'text-pink-400',
      bg: 'bg-pink-500/20',
      title: "Mode Couples",
      desc: "Déjà ensemble grâce à Heartbeats ? Passez en mode couple et partagez votre histoire."
    },
    {
      icon: Sparkles,
      color: 'text-orange-400',
      bg: 'bg-orange-500/20',
      title: "Parrainage",
      desc: "Invite tes amis, gagne des avantages. Construis ta communauté et profite ensemble."
    }
  ];

  // Stats animées
  const AnimatedNumber = ({ value }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
      const end = parseInt(value);
      let start = 0;
      const timer = setInterval(() => {
        start += Math.ceil(end / 30);
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, 40);
      return () => clearInterval(timer);
    }, [value]);
    return <span>{count.toLocaleString('fr-FR')}</span>;
  };

  return (
    <div 
      className="min-h-screen bg-[#0a0015] text-white"
      style={{
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y'
      }}
      data-testid="heartbeats-landing"
    >
      {/* Fond avec gradient subtil */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#dc2626]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[150px]" />
      </div>

      {/* Cœurs flottants subtils */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={`heart-${i}`}
            className="absolute text-[#dc2626]/20"
            style={{
              left: `${5 + (i * 8) % 90}%`,
              bottom: '-30px',
              fontSize: `${10 + (i % 3) * 4}px`,
            }}
            animate={{
              y: [0, -window.innerHeight - 100],
              rotate: [0, 180],
              opacity: [0.2, 0.4, 0.2, 0],
            }}
            transition={{
              duration: 20 + (i % 5) * 4,
              repeat: Infinity,
              delay: i * 1.5,
              ease: 'linear',
            }}
          >
            <Heart fill="currentColor" />
          </motion.div>
        ))}
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0a0015]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Heart className="w-7 h-7 text-[#dc2626]" fill="currentColor" />
              <span className="text-xl font-bold tracking-tight">
                <span className="text-white">Heart</span>
                <span className="text-[#dc2626]">beats</span>
              </span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('concept')} className="text-gray-400 hover:text-white transition-colors text-sm">
                Le Concept
              </button>
              <button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-white transition-colors text-sm">
                Fonctionnalités
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-400 hover:text-white transition-colors text-sm">
                Tarifs
              </button>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <button onClick={handleLogin} className="text-gray-400 hover:text-white transition-colors text-sm">
                Connexion
              </button>
              <button 
                onClick={handleGetStarted}
                className="px-5 py-2.5 bg-[#dc2626] hover:bg-[#b91c1c] rounded-lg font-medium transition-colors text-sm"
              >
                Commencer
              </button>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-[#0a0015] border-t border-white/5 py-4 px-4"
          >
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('concept')} className="text-left text-gray-400 py-2">Le Concept</button>
              <button onClick={() => scrollToSection('features')} className="text-left text-gray-400 py-2">Fonctionnalités</button>
              <button onClick={() => scrollToSection('pricing')} className="text-left text-gray-400 py-2">Tarifs</button>
              <hr className="border-white/10" />
              <button onClick={handleLogin} className="text-left text-gray-400 py-2">Connexion</button>
              <button onClick={handleGetStarted} className="bg-[#dc2626] py-3 rounded-xl font-medium text-center">Commencer</button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[90vh] flex items-center py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400 mb-8">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              {stats.onlineNow} personnes en ligne maintenant
            </div>

            {/* Titre principal */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight">
              L'amour mérite<br />
              <span className="bg-gradient-to-r from-[#dc2626] to-[#ec4899] bg-clip-text text-transparent">
                mieux qu'un swipe.
              </span>
            </h1>

            {/* Sous-titre */}
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
              Heartbeats réinvente la rencontre. Pas de jugement en 2 secondes.<br />
              Des vraies conversations, des vraies personnes, près de chez toi.
            </p>

            {/* CTA Principal - Direct vers inscription */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <button
                onClick={handleGetStarted}
                className="px-8 py-4 bg-[#dc2626] hover:bg-[#b91c1c] rounded-xl font-bold text-lg transition-all flex items-center gap-3 shadow-lg shadow-[#dc2626]/30"
              >
                <Heart className="w-5 h-5" fill="currentColor" />
                Créer mon compte gratuit
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogin}
                className="px-6 py-4 bg-white/10 hover:bg-white/15 rounded-xl font-medium transition-all border border-white/20"
              >
                J'ai déjà un compte
              </button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <AnimatedNumber value={stats.totalUsers} /> membres
              </div>
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                {(stats.conversations || 3420).toLocaleString('fr-FR')} conversations/jour
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                100% profils vérifiés
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== LE PROBLÈME ==================== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-300">
              Marre des apps de rencontre ?
            </h2>
            <p className="text-xl text-gray-500 leading-relaxed max-w-2xl mx-auto">
              Swipe à gauche, swipe à droite. Des centaines de profils.<br />
              Des conversations qui meurent après "Salut ça va ?".<br />
              Des matchs qui ne répondent jamais.<br />
              <span className="text-[#dc2626] font-semibold">Tu mérites mieux que ça.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ==================== LES 4 PILIERS ==================== */}
      <section id="concept" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Une approche <span className="text-[#dc2626]">radicalement</span> différente.
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              On a repensé la rencontre de zéro. Humaine. Respectueuse. Authentique.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {pillars.map((pillar, index) => (
              <motion.div
                key={`pillar-${pillar.title}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-3xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${pillar.gradient} flex items-center justify-center mb-6`}>
                  <pillar.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-1">{pillar.title}</h3>
                <h3 className="text-2xl font-bold text-gray-500 mb-4">{pillar.subtitle}</h3>
                <p className="text-gray-400 leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== EMAIL CAPTURE MILIEU ==================== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-[#dc2626]/10 to-purple-600/10 border border-white/10"
        >
          <Sparkles className="w-12 h-12 text-[#dc2626] mx-auto mb-4" />
          <h3 className="text-2xl sm:text-3xl font-bold mb-2">
            Prêt à tenter l'aventure ?
          </h3>
          <p className="text-gray-400 mb-6">
            Rejoins des milliers de célibataires qui cherchent l'amour autrement.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleGetStarted}
              className="px-8 py-4 bg-[#dc2626] hover:bg-[#b91c1c] rounded-xl font-bold transition-all flex items-center gap-2"
            >
              <Heart className="w-5 h-5" fill="currentColor" />
              Créer mon compte
            </button>
            <button
              onClick={handleLogin}
              className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
            >
              Déjà inscrit ? Se connecter
            </button>
          </div>
        </motion.div>
      </section>

      {/* ==================== FONCTIONNALITÉS UNIQUES ==================== */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-white/[0.02]">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Des fonctionnalités <span className="text-[#dc2626]">uniques.</span>
            </h2>
            <p className="text-gray-500 text-lg">
              Ce que tu ne trouveras nulle part ailleurs.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {uniqueFeatures.map((feature, index) => (
              <motion.div
                key={`feature-${feature.title}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                </div>
                <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#dc2626]/10 border border-[#dc2626]/20 text-sm mb-6">
              <Heart className="w-4 h-4 text-[#dc2626]" fill="currentColor" />
              <span className="text-[#22d3ee]">💎</span>
              <span className="text-gray-400">NOS OFFRES</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Choisissez votre <span className="text-[#dc2626]">vibration.</span>
            </h2>
            <p className="text-gray-500 text-lg">
              Commencez gratuitement. Montez en puissance quand vous êtes prêt.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* GRATUIT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-3xl bg-white/[0.03] border border-white/10"
            >
              <div className="text-sm font-medium text-gray-500 mb-4">GRATUIT</div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-black">0€</span>
                <span className="text-gray-500">/mois</span>
              </div>
              <p className="text-gray-500 text-sm mb-6">Pour découvrir Heartbeats</p>
              
              <ul className="space-y-3 mb-8">
                {[
                  { text: 'Profil de base', included: true },
                  { text: '5 matchs par jour', included: true },
                  { text: 'Accès aux profils locaux', included: true },
                  { text: 'Chat basique', included: true },
                  { text: 'Guides Pixie & Enzo', included: false },
                  { text: 'Voir qui vous a liké', included: false },
                  { text: 'Boost de profil', included: false }
                ].map((item) => (
                  <li key={`free-${item.text}`} className="flex items-center gap-3 text-sm">
                    {item.included ? (
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    )}
                    <span className={item.included ? 'text-gray-300' : 'text-gray-600 line-through'}>{item.text}</span>
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={handleGetStarted}
                className="w-full py-3.5 rounded-xl bg-white/10 hover:bg-white/15 font-semibold transition-colors border border-white/10"
              >
                Commencer gratuitement
              </button>
            </motion.div>

            {/* PREMIUM - POPULAIRE */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-3xl bg-gradient-to-b from-[#dc2626]/10 to-transparent border border-[#dc2626]/30 relative"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#dc2626] rounded-full text-sm font-bold flex items-center gap-1">
                <span className="text-yellow-300">★</span>
                LE PLUS POPULAIRE
              </div>
              
              <div className="text-sm font-medium text-[#dc2626] mb-4 pt-2">PREMIUM</div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-black">9,99€</span>
                <span className="text-gray-500">/mois</span>
              </div>
              <p className="text-gray-500 text-sm mb-6">Pour les sérieux de l'amour</p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Matchs illimités',
                  'Voir qui vous a liké',
                  'Guide Pixie activé',
                  'Boost de profil mensuel',
                  'Super Like × 5/jour',
                  'Filtres avancés de proximité'
                ].map((item) => (
                  <li key={`premium-${item}`} className="flex items-center gap-3 text-sm">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={handleGetStarted}
                className="w-full py-3.5 rounded-xl bg-[#dc2626] hover:bg-[#b91c1c] font-semibold transition-colors"
              >
                Commencer Premium →
              </button>
            </motion.div>

            {/* VIP */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-3xl bg-gradient-to-b from-purple-600/10 to-transparent border border-purple-500/30"
            >
              <div className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                <Crown className="w-4 h-4 text-purple-400" />
                VIP
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-black">19,99€</span>
                <span className="text-gray-500">/mois</span>
              </div>
              <p className="text-gray-500 text-sm mb-6">Pour les conquérants du cœur</p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Tout Premium inclus',
                  'Pixie & Enzo (les deux)',
                  'Mode invisible',
                  'Matchs prioritaires',
                  'Profil vérifié VIP',
                  'Support dédié 24/7'
                ].map((item) => (
                  <li key={`vip-${item}`} className="flex items-center gap-3 text-sm">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={handleGetStarted}
                className="w-full py-3.5 rounded-xl bg-purple-600 hover:bg-purple-700 font-semibold transition-colors"
              >
                Passer VIP
              </button>
            </motion.div>
          </div>

          <p className="text-center text-gray-600 text-sm mt-8">
            Sans engagement · Résiliable à tout moment · Paiement sécurisé
          </p>
        </div>
      </section>

      {/* ==================== CTA FINAL + EMAIL ==================== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#dc2626] to-[#ec4899] flex items-center justify-center mx-auto mb-8">
            <Heart className="w-10 h-10 text-white" fill="currentColor" />
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-black mb-4">
            Prêt à rencontrer<br />
            <span className="text-[#dc2626]">autrement ?</span>
          </h2>
          
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Rejoins une communauté qui croit que l'amour mérite du respect, du temps et de l'authenticité.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <button
              onClick={handleGetStarted}
              className="px-10 py-5 bg-[#dc2626] hover:bg-[#b91c1c] rounded-2xl font-bold text-xl transition-all flex items-center gap-3 shadow-xl shadow-[#dc2626]/40"
            >
              <Heart className="w-6 h-6" fill="currentColor" />
              Commencer maintenant
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
          
          <button
            onClick={handleLogin}
            className="text-gray-500 hover:text-white transition-colors mb-4"
          >
            Déjà membre ? Se connecter →
          </button>

          <p className="text-gray-600 text-sm">
            Gratuit • Sans engagement • Respect de ta vie privée
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#dc2626]" fill="currentColor" />
            <span className="text-sm text-gray-500">© 2026 Heartbeats — L'amour mérite mieux.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <a href="/mentions-legales?from=heartbeats" className="hover:text-white transition-colors">Mentions légales</a>
            <a href="/cgu?from=heartbeats" className="hover:text-white transition-colors">CGU</a>
            <a href="/confidentialite?from=heartbeats" className="hover:text-white transition-colors">Confidentialité</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HeartbeatsLanding;
